import { useCallback, useRef, useState } from "react";
import { AxiosInstance } from "axios";
import { searchStaffByName } from "@/services/user/User";
import { User } from "@/types/common/user";

export function useStaffSearch(apiClient: AxiosInstance) {
  const [staffSearchQuery, setStaffSearchQuery] = useState("");
  const [staffResults, setStaffResults] = useState<User[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<User | null>(null);
  const [staffSearchLoading, setStaffSearchLoading] = useState(false);
  const staffDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleStaffSearchChange = useCallback(
    (value: string) => {
      setStaffSearchQuery(value);
      setSelectedStaff(null);
      if (staffDebounceRef.current) clearTimeout(staffDebounceRef.current);
      if (value.length < 2) {
        setStaffResults([]);
        return;
      }
      setStaffSearchLoading(true);
      staffDebounceRef.current = setTimeout(async () => {
        try {
          const res = await searchStaffByName(apiClient, value);
          setStaffResults(res.data?.value ?? res.data ?? []);
        } catch {
          setStaffResults([]);
        } finally {
          setStaffSearchLoading(false);
        }
      }, 300);
    },
    [apiClient],
  );

  const handleStaffSelect = useCallback(
    (userId: string) => {
      const staff = staffResults.find((s) => String(s.Id) === userId);
      if (!staff) return;
      setSelectedStaff(staff);
      setStaffSearchQuery(staff.FullName);
      setStaffResults([]);
    },
    [staffResults],
  );

  const resetStaffSearch = useCallback(() => {
    setSelectedStaff(null);
    setStaffSearchQuery("");
    setStaffResults([]);
    if (staffDebounceRef.current) clearTimeout(staffDebounceRef.current);
  }, []);

  return {
    staffSearchQuery,
    staffResults,
    selectedStaff,
    staffSearchLoading,
    handleStaffSearchChange,
    handleStaffSelect,
    resetStaffSearch,
  };
}
